import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, CaretLeft, ArrowLeft, CalendarBlank, User, Sparkle } from "@phosphor-icons/react";
import { getArticleBySlug, ARTICLES, ARTICLE_CATEGORIES } from "../../data/articles/articlesData";
import usePageSeo from "../../hooks/usePageSeo";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import ContentLoader from "../../components/shared/ContentLoader";
import "./ArticlePage.css";

// Strips markdown syntax (bold, links, inline code) to plain text for schema output
const stripMarkdown = (text) =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]*)\*\*/g, "$1")
    .replace(/\*([^*]*)\*/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

// Parses the "## שאלות נפוצות" section into schema.org Question entities
const extractFaqFromMarkdown = (markdown) => {
  if (!markdown) return [];
  const faqMatch = markdown.match(/^## שאלות נפוצות\s*$([\s\S]*?)(?=^## |(?![\s\S]))/m);
  if (!faqMatch) return [];

  const entities = [];
  const qaRegex = /^### (.+)$\n([\s\S]*?)(?=^### |(?![\s\S]))/gm;
  let qa;
  while ((qa = qaRegex.exec(faqMatch[1])) !== null) {
    const name = stripMarkdown(qa[1]);
    const answer = stripMarkdown(qa[2].replace(/^---\s*$/gm, ""));
    if (name && answer) {
      entities.push({
        "@type": "Question",
        name,
        acceptedAnswer: { "@type": "Answer", text: answer },
      });
    }
  }
  return entities;
};

function ArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  usePageSeo({
    title: article ? `${article.title}` : "מאמר לא נמצא",
    description: article?.description || "",
    path: `/blog/${slug}`,
    ogType: "article",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;

    const setMeta = (attr, key, val) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    setMeta("name", "keywords", article.keywords.join(", "));
    setMeta("property", "article:published_time", article.date);
    setMeta("property", "article:author", article.author);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      dateModified: article.date,
      author: {
        "@type": "Organization",
        name: article.author,
        url: "https://clinikit.co.il",
      },
      publisher: {
        "@type": "Organization",
        name: "CliniKit",
        alternateName: "קליניקיט",
        url: "https://clinikit.co.il",
        logo: {
          "@type": "ImageObject",
          url: "https://clinikit.co.il/favicon.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://clinikit.co.il/blog/${article.slug}`,
      },
      keywords: article.keywords.join(", "),
      wordCount: content.split(/\s+/).length,
      timeRequired: `PT${article.readTime}M`,
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // FAQPage schema from the article's "שאלות נפוצות" section (if present)
    try {
      const faqEntities = extractFaqFromMarkdown(content);
      if (faqEntities.length > 0) {
        const faqScript = document.createElement("script");
        faqScript.type = "application/ld+json";
        faqScript.id = "article-faq-jsonld";
        faqScript.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqEntities,
        });
        document.head.appendChild(faqScript);
      }
    } catch (error) {
      console.error("[CK-6110] Failed to build FAQ structured data", {
        errorCode: error.code,
        errorMessage: error.message,
        slug: article.slug,
        timestamp: new Date().toISOString(),
      });
    }

    return () => {
      const el = document.getElementById("article-jsonld");
      if (el) el.remove();
      const faqEl = document.getElementById("article-faq-jsonld");
      if (faqEl) faqEl.remove();
    };
  }, [article, slug, content]);

  useEffect(() => {
    if (!article) return;

    setIsLoading(true);
    fetch(`/articles/${article.slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error("Article not found");
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setIsLoading(false);
      })
      .catch(() => {
        setContent("");
        setIsLoading(false);
      });
  }, [article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getProfessionLabel = (value) => {
    const found = ARTICLE_CATEGORIES.professions.find((p) => p.value === value);
    return found ? found.label : value;
  };

  const getTopicLabel = (value) => {
    const found = ARTICLE_CATEGORIES.topics.find((t) => t.value === value);
    return found ? found.label : value;
  };

  const relatedArticles = ARTICLES.filter(
    (a) =>
      a.slug !== article.slug &&
      (a.profession === article.profession || a.topic === article.topic)
  ).slice(0, 3);

  return (
    <div className="article-page">
      <Header />

      <main>
        {/* Breadcrumb */}
        <nav className="article-breadcrumb" aria-label="Breadcrumb">
          <div className="container">
            <ol itemScope itemType="https://schema.org/BreadcrumbList">
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <Link to="/" itemProp="item">
                  <span itemProp="name">דף הבית</span>
                </Link>
                <meta itemProp="position" content="1" />
                <CaretLeft size={14} weight="bold" />
              </li>
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <Link to="/blog" itemProp="item">
                  <span itemProp="name">מאמרים</span>
                </Link>
                <meta itemProp="position" content="2" />
                <CaretLeft size={14} weight="bold" />
              </li>
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
                aria-current="page"
              >
                <span itemProp="name">{article.title}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        <article className="article-content-wrapper" itemScope itemType="https://schema.org/BlogPosting">
          <div className="container">
            <div className="article-layout">
              {/* Main Content */}
              <div className="article-main">
                {/* Article Header */}
                <header className="article-header">
                  <div className="article-header-tags">
                    <span className="article-tag article-tag-profession">
                      {getProfessionLabel(article.profession)}
                    </span>
                    <span className="article-tag article-tag-topic">
                      {getTopicLabel(article.topic)}
                    </span>
                  </div>

                  <h1 itemProp="headline">{article.title}</h1>
                  <p className="article-header-description" itemProp="description">
                    {article.description}
                  </p>

                  <div className="article-header-meta">
                    <span className="article-meta-item">
                      <CalendarBlank size={16} weight="regular" />
                      <time dateTime={article.date} itemProp="datePublished">
                        {formatDate(article.date)}
                      </time>
                    </span>
                    <span className="article-meta-item">
                      <User size={16} weight="regular" />
                      <span itemProp="author">{article.author}</span>
                    </span>
                    <span className="article-meta-item">
                      <Clock size={16} weight="regular" />
                      {article.readTime} דקות קריאה
                    </span>
                  </div>
                </header>

                {/* Article Body */}
                <div className="article-body" itemProp="articleBody">
                  {isLoading ? (
                    <ContentLoader fill message="טוען מאמר..." />
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children, ...props }) => {
                          if (href && href.startsWith("/")) {
                            return <Link to={href} {...props}>{children}</Link>;
                          }
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  )}
                </div>

                {/* CTA */}
                <div className="article-inline-cta">
                  <div className="article-inline-cta-badge">
                    <Sparkle size={14} weight="fill" />
                    30 ימי ניסיון חינם
                  </div>
                  <h3>מוכנים לנהל את הקליניקה בצורה חכמה?</h3>
                  <p>נסו את CliniKit 30 ימים חינם – ללא כרטיס אשראי</p>
                  <Link to="/#lead-form" className="article-cta-btn">
                    קבעו שיחת הדרכה
                    <ArrowLeft size={16} weight="bold" />
                  </Link>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="article-sidebar">
                <div className="article-sidebar-sticky">
                  {/* Keywords */}
                  <div className="article-sidebar-card">
                    <h4>מילות מפתח</h4>
                    <div className="article-keywords">
                      {article.keywords.map((kw) => (
                        <span key={kw} className="article-keyword">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Related Articles */}
                  {relatedArticles.length > 0 && (
                    <div className="article-sidebar-card">
                      <h4>מאמרים נוספים</h4>
                      <div className="article-related-list">
                        {relatedArticles.map((related) => (
                          <Link
                            key={related.slug}
                            to={`/blog/${related.slug}`}
                            className="article-related-item"
                          >
                            {related.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Back to blog */}
                  <Link to="/blog" className="article-back-link">
                    <ArrowLeft size={14} weight="bold" style={{ transform: "rotate(180deg)" }} />
                    חזרה לכל המאמרים
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default ArticlePage;
